import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { NoticesAPI } from "@/lib/sprint7-api";
import {
  InstitutionsAPI,
  CampusesAPI,
  ClassesAPI,
  SectionsAPI,
  YearsAPI,
  TenantsAPI,
} from "@/lib/sprint2-api";
import { StudentsAPI } from "@/lib/sprint3-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, X, FileText, Image as ImageIcon, Video, ChevronLeft, Loader2 } from "lucide-react";

const NOTICE_TYPES = [
  { value: "general", label: "General" },
  { value: "exam", label: "Exam" },
  { value: "holiday", label: "Holiday" },
  { value: "event", label: "Event" },
  { value: "fees", label: "Fees" },
  { value: "circular", label: "Circular" },
  { value: "emergency", label: "Emergency" },
];

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

function useTenantId() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({
    queryKey: ["cn-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  if (!isSuper) return user?.tenant_id || null;
  return tQ.data?.[0]?.id || null;
}

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return <ImageIcon className="h-4 w-4" />;
  if (ct.startsWith("video/")) return <Video className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

function humanSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function CreateNotice() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tenantId = useTenantId();
  const tParam = tenantId ? { tenant_id: tenantId } : {};

  // Form state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [noticeType, setNoticeType] = useState("general");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Audience: cascading filters
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("");
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [studentId, setStudentId] = useState("");

  // Lookups
  const instQ = useQuery({
    queryKey: ["cn-inst", tenantId],
    queryFn: () => InstitutionsAPI.list(tParam),
    enabled: !!tenantId,
  });
  const campusesQ = useQuery({
    queryKey: ["cn-camp", tenantId, institutionId],
    queryFn: () => CampusesAPI.list({ ...tParam, institution_id: institutionId }),
    enabled: !!tenantId && !!institutionId,
  });
  const yearsQ = useQuery({
    queryKey: ["cn-yrs", tenantId, institutionId],
    queryFn: () => YearsAPI.list({ ...tParam, institution_id: institutionId }),
    enabled: !!tenantId && !!institutionId,
  });
  const classesQ = useQuery({
    queryKey: ["cn-cls", tenantId, yearId, campusId],
    queryFn: () => ClassesAPI.list({ ...tParam, academic_year_id: yearId, ...(campusId ? { campus_id: campusId } : {}) }),
    enabled: !!tenantId && !!yearId,
  });
  const sectionsQ = useQuery({
    queryKey: ["cn-sec", tenantId, classId],
    queryFn: () => SectionsAPI.list({ ...tParam, class_id: classId }),
    enabled: !!tenantId && !!classId,
  });
  const studentsQ = useQuery({
    queryKey: ["cn-stu", tenantId, sectionId],
    queryFn: () => StudentsAPI.list({ ...tParam, section_id: sectionId, limit: 500 }),
    enabled: !!tenantId && !!sectionId,
  });

  // Resolve audience type + ids based on the most specific selector filled.
  const resolved = useMemo(() => {
    if (studentId) return { type: "student", ids: [studentId], label: "Individual student" };
    if (sectionId) return { type: "section", ids: [sectionId], label: "Section" };
    if (classId) return { type: "class", ids: [classId], label: "Class" };
    if (campusId) return { type: "campus", ids: [campusId], label: "Campus" };
    if (institutionId) return { type: "institution", ids: [institutionId], label: "Institute" };
    return { type: "everyone", ids: [], label: "Everyone in tenant" };
  }, [institutionId, campusId, classId, sectionId, studentId]);

  // Upload handler
  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — max 50MB. (${humanSize(file.size)})`);
      return;
    }
    setUploading(true);
    try {
      const res = await NoticesAPI.uploadAttachment(file);
      setAttachments((a) => [...a, res]);
      toast.success(`Uploaded ${res.filename}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };

  // Strip HTML/whitespace to validate the rich-text body is non-empty.
  const plainBody = useMemo(
    () => (body || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim(),
    [body]
  );

  const submit = useMutation({
    mutationFn: () =>
      NoticesAPI.create({
        title: title.trim(),
        body: plainBody ? body : title.trim(), // fall back to title if body empty
        notice_type: noticeType,
        audience_type: resolved.type,
        audience_ids: resolved.ids,
        attachments: attachments.map((a) => ({
          id: a.id,
          filename: a.filename,
          content_type: a.content_type,
          size: a.size,
        })),
        tenant_id: tenantId || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`Notice published to ${data.recipients} recipient${data.recipients === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["notices"] });
      navigate("/notices");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e)),
  });

  const canSubmit = title.trim().length >= 2 && !uploading && !submit.isPending;

  return (
    <AppShell>
      <div className="p-6 max-w-[1100px] mx-auto" data-testid="create-notice-page">
        {/* Breadcrumb-ish header */}
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <Link to="/notices" className="hover:text-foreground flex items-center gap-1" data-testid="back-to-notices">
            <ChevronLeft className="h-4 w-4" /> Notices
          </Link>
          <span>/</span>
          <span className="text-foreground">Create Notice</span>
        </div>

        {/* Audience filter row */}
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-3">
            Target audience · empty selectors mean broader scope
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-[11px]">Institute</Label>
              <Select value={institutionId || "all"} onValueChange={(v) => { setInstitutionId(v === "all" ? "" : v); setCampusId(""); setYearId(""); setClassId(""); setSectionId(""); setStudentId(""); }}>
                <SelectTrigger data-testid="cn-institute"><SelectValue placeholder="All institutes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All institutes</SelectItem>
                  {(instQ.data || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Campus</Label>
              <Select value={campusId || "all"} disabled={!institutionId} onValueChange={(v) => { setCampusId(v === "all" ? "" : v); setClassId(""); setSectionId(""); setStudentId(""); }}>
                <SelectTrigger data-testid="cn-campus"><SelectValue placeholder="All campuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campuses</SelectItem>
                  {(campusesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Year</Label>
              <Select value={yearId || "_"} disabled={!institutionId} onValueChange={(v) => { setYearId(v === "_" ? "" : v); setClassId(""); setSectionId(""); setStudentId(""); }}>
                <SelectTrigger data-testid="cn-year"><SelectValue placeholder="Pick year…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">Pick year…</SelectItem>
                  {(yearsQ.data || []).map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Class</Label>
              <Select value={classId || "all"} disabled={!yearId} onValueChange={(v) => { setClassId(v === "all" ? "" : v); setSectionId(""); setStudentId(""); }}>
                <SelectTrigger data-testid="cn-class"><SelectValue placeholder="All classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {(classesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.campus_name ? ` · ${c.campus_name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Section</Label>
              <Select value={sectionId || "all"} disabled={!classId} onValueChange={(v) => { setSectionId(v === "all" ? "" : v); setStudentId(""); }}>
                <SelectTrigger data-testid="cn-section"><SelectValue placeholder="All sections" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sections</SelectItem>
                  {(sectionsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Individual student</Label>
              <Select value={studentId || "all"} disabled={!sectionId} onValueChange={(v) => setStudentId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="cn-student"><SelectValue placeholder="Whole section" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Whole section</SelectItem>
                  {(studentsQ.data || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name || s.name} {s.last_name || ""} {s.roll_no ? `· #${s.roll_no}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            Resolved target:
            <Badge variant="outline" data-testid="cn-resolved-target">{resolved.label}</Badge>
          </div>
        </div>

        {/* Form body */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-6">
          <h1 className="font-display font-extrabold tracking-tight text-2xl">Create a Notice</h1>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 items-start border-b pb-5">
            <Label className="pt-2 font-semibold">Notice Subject</Label>
            <Input
              data-testid="cn-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Write notice caption here"
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 items-start border-b pb-5">
            <Label className="pt-2 font-semibold">Type of Notice</Label>
            <Select value={noticeType} onValueChange={setNoticeType}>
              <SelectTrigger className="md:max-w-xs" data-testid="cn-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {NOTICE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 items-start border-b pb-5">
            <Label className="pt-2 font-semibold">Attachments<br /><span className="text-[10px] font-normal text-muted-foreground">Doc / Image / Video · max 50MB</span></Label>
            <div>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/40 transition"
                data-testid="cn-dropzone"
              >
                <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground mb-2" />
                <div className="text-sm font-medium">Drag &amp; drop file here, or click to upload</div>
                <div className="text-[11px] text-muted-foreground mt-1">Supported: PDF, DOCX, JPG, PNG, MP4 — up to 50MB</div>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  onChange={(e) => onUpload(e.target.files?.[0])}
                  data-testid="cn-file-input"
                />
                {uploading && <div className="mt-2 text-xs text-primary flex items-center justify-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</div>}
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 space-y-3" data-testid="cn-attachments-list">
                  {/* Inline image previews (large) */}
                  {attachments.filter((a) => (a.content_type || "").startsWith("image/")).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Put Images Here!</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {attachments.filter((a) => (a.content_type || "").startsWith("image/")).map((a) => (
                          <div key={a.id} className="relative group border border-border rounded-md overflow-hidden bg-muted/30" data-testid={`cn-img-${a.id}`}>
                            <img src={NoticesAPI.attachmentUrl(a.id)} alt={a.filename} className="w-full h-44 object-cover" />
                            <button
                              type="button"
                              onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))}
                              className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:bg-black"
                              data-testid={`cn-remove-${a.id}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                            <div className="absolute bottom-0 inset-x-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] flex items-center justify-between">
                              <span className="truncate">{a.filename}</span>
                              <span className="text-white/70">{humanSize(a.size)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Non-image attachments — keep the chip view */}
                  {attachments.filter((a) => !(a.content_type || "").startsWith("image/")).length > 0 && (
                    <ul className="space-y-1.5">
                      {attachments.filter((a) => !(a.content_type || "").startsWith("image/")).map((a) => (
                        <li key={a.id} className="flex items-center gap-2 px-3 py-2 border border-border rounded-md bg-muted/30 text-sm">
                          {fileIcon(a.content_type)}
                          <span className="flex-1 truncate">{a.filename}</span>
                          <span className="text-xs text-muted-foreground">{humanSize(a.size)}</span>
                          <button
                            type="button"
                            onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))}
                            className="text-muted-foreground hover:text-destructive transition"
                            data-testid={`cn-remove-${a.id}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 items-start">
            <Label className="pt-2 font-semibold">Notice Description</Label>
            <div className="quill-wrapper" data-testid="cn-body">
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                placeholder="Start writing here…"
                modules={{
                  toolbar: [
                    [{ header: [false, 2, 3] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "blockquote"],
                    [{ color: [] }, { background: [] }],
                    ["clean"],
                  ],
                }}
                formats={["header", "bold", "italic", "underline", "strike", "list", "align", "link", "blockquote", "color", "background"]}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => navigate("/notices")} data-testid="cn-cancel">Cancel</Button>
            <Button
              onClick={() => submit.mutate()}
              disabled={!canSubmit}
              data-testid="cn-submit"
            >
              {submit.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
